import {
  Autocomplete,
  Box,
  // Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import {
  useEffect,
  // useState 
} from "react";
import { getCountry, getEthnicityTypes, getGenderTypes, getRaceTypes, getStates, } from "store/lookup";
import { useDispatch, useSelector } from "react-redux";
import RequiredLabel from "components/RequiredLabel";
import PhoneField from 'components/PhoneField'
import { useTranslation } from "react-i18next";

const militaryStatusOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const livesWithOptions = [
  { label: "Both Parents", value: "Both Parents" },
  { label: "Mother", value: "Mother" },
  { label: "Father", value: "Father" },
  { label: "Guardian", value: "Guardian" },
];


const phoneTypes = [
  { label: "Mobile", value: "mobile" },
  { label: "Home", value: "home" },
  { label: "Work", value: "work" },
];

export default function StudentInformation() {
  const { control, watch } = useFormContext();
  const countryOfBirth = watch("countryOfBirth");
  const genderOptions = useSelector(state => state.lookup?.genderTypes || []);
  const ethnicityOptions = useSelector(state => state.lookup?.ethnicityTypes || []);
  const raceOptions = useSelector(state => state.lookup?.raceTypes || []);
  const countryOptions = useSelector((state) => state.lookup?.country || state.staff?.country || []);
  const stateOptions = useSelector((state) => state.lookup?.states || state.staff?.states || []);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getCountry());
    dispatch(getStates());
    dispatch(getGenderTypes());
    dispatch(getEthnicityTypes());
    dispatch(getRaceTypes());
  }, [dispatch]);

  const ParentFields = ({ prefix }) => {


    return (
      <Grid container spacing={2} pt={1}>
        <Grid item xs={12} md={2.5}>
          <Controller
            name={`${prefix}.firstName`}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={<RequiredLabel>{t('First Name')}</RequiredLabel>}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={2.5}>
          <Controller
            name={`${prefix}.lastName`}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={<RequiredLabel>{t('Last Name')}</RequiredLabel>}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <Controller
            name={`${prefix}.phoneType`}
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={phoneTypes}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === (value?.value ?? value)
                }
                value={phoneTypes.find((opt) => opt.value === field.value) || null}
                onChange={(_, v) => field.onChange(v ? v.value : "")}
                renderInput={(params) => (
                  <TextField {...params} label={<RequiredLabel>{t('Phone Type')}</RequiredLabel>} />
                )}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={2.5}>
          <PhoneField
            name={`${prefix}.phoneNumber`}
            control={control}
            label={t('Phone Number')}
            required
          />
        </Grid>


        <Grid item xs={12} md={2.5}>
          <Controller
            name={`${prefix}.email`}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={<RequiredLabel>{t('Email Address')}</RequiredLabel>}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
      </Grid>
    );
  };

  const isUnitedStates =
    countryOfBirth === "United States" ||
    (countryOfBirth && typeof countryOfBirth === "object" && countryOfBirth.countryName === "United States") ||
    (countryOfBirth && typeof countryOfBirth === "string" && countryOfBirth === "US") ||
    (countryOfBirth && typeof countryOfBirth === "string" && countryOfBirth === "2330");

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
        <Box
          backgroundColor="divider"
          sx={{ p: 2, borderTopLeftRadius: 8, borderTopRightRadius: 8 }}
        >
          <Typography variant="h6">{t("Student Information")}</Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />

        <Grid container spacing={2}>

          <Grid item xs={12} md={3}>
            <Controller
              name="streetAddress"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={<RequiredLabel>{t('Street Address')}</RequiredLabel>}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Controller
              name="addressLine2"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('Address Line 2')}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          {/* required */}
          <Grid item xs={12} md={3}>
            <Controller
              name="city"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={<RequiredLabel>{t('City')}</RequiredLabel>}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Controller
              name="state"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  options={stateOptions || []}
                  getOptionLabel={(opt) => opt?.stateName || ""}
                  isOptionEqualToValue={(opt, val) =>
                    (opt?.stateAbbr ?? opt) === (val?.stateAbbr ?? val)
                  }
                  value={
                    (stateOptions || []).find((s) => s.stateAbbr === field.value) || null
                  }
                  onChange={(_, v) => field.onChange(v ? v.stateAbbr : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<RequiredLabel>{t('State')}</RequiredLabel>}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Controller
              name="zipCode"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={<RequiredLabel>{t('Zip Code')}</RequiredLabel>}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={!isUnitedStates ? 4 : 3}>
            <Controller
              name="livesWith"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  options={livesWithOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === (value?.value ?? value)
                  }
                  value={
                    livesWithOptions.find((opt) => opt.value === field.value) || null
                  }
                  onChange={(_, newValue) =>
                    field.onChange(newValue ? newValue.value : "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<RequiredLabel>{t('Student Lives With')}</RequiredLabel>}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  )}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={!isUnitedStates ? 4 : 3}>
            <Controller
              name="countryOfBirth"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  options={countryOptions || []}
                  getOptionLabel={(opt) => opt?.countryName || ""}
                  isOptionEqualToValue={(opt, val) =>
                    (opt?.countryCode ?? opt) === (val?.countryCode ?? val)
                  }
                  value={
                    (countryOptions || []).find(
                      (c) =>
                        c.countryCode === field.value ||
                        c.countryName === field.value ||
                        c === field.value
                    ) || field.value || null
                  }
                  onChange={(_, v) => field.onChange(v ? v.countryCode : "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<RequiredLabel>{t('Country of Birth')}</RequiredLabel>}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>

          {isUnitedStates && (
            <Grid item xs={12} md={3}>
              <Controller
                name="stateOfBirth"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={stateOptions || []}
                    getOptionLabel={(opt) => opt?.stateName || ""}
                    isOptionEqualToValue={(opt, val) =>
                      (opt?.stateAbbr ?? opt) === (val?.stateAbbr ?? val)
                    }
                    value={
                      (stateOptions || []).find((s) => s.stateAbbr === field.value) ||
                      null
                    }
                    onChange={(_, v) => field.onChange(v ? v.stateAbbr : "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={<RequiredLabel>{t("State of Birth")}</RequiredLabel>}
                        fullWidth
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={12} md={!isUnitedStates ? 4 : 3}>
            <Controller
              name="cityOfBirth"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t("City of Birth")}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Controller
              name="gender"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  options={genderOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === (value?.id ?? value)
                  }
                  value={
                    genderOptions.find((opt) => opt.id === field.value) || null
                  }
                  onChange={(_, newValue) =>
                    field.onChange(newValue ? newValue.id : "")
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<RequiredLabel>{t('Gender')}</RequiredLabel>}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Controller
              name="race"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  options={raceOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === (value?.id ?? value)
                  }
                  value={
                    Array.isArray(field.value)
                      ? raceOptions.filter((opt) => field.value.includes(opt.id))
                      : []
                  }
                  onChange={(_, newValue) =>
                    field.onChange(newValue ? newValue.map((opt) => opt.id) : [])
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<RequiredLabel>{t('Race')}</RequiredLabel>}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Controller
              name="ethnicity"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  options={ethnicityOptions}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === (value?.id ?? value)
                  }
                  value={
                    ethnicityOptions.find((opt) => opt.id === field.value) || null
                  }
                  onChange={(_, newValue) =>
                    field.onChange(newValue ? newValue.id : null)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<RequiredLabel>{t('Ethnicity')}</RequiredLabel>}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  )}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Controller
              name="militaryStatus"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  options={militaryStatusOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === (value?.value ?? value)
                  }
                  value={
                    militaryStatusOptions.find((opt) => opt.value === field.value) ??
                    null
                  }
                  onChange={(_, newValue) =>
                    field.onChange(newValue ? newValue.value : null)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<RequiredLabel>{t('Military Status')}</RequiredLabel>}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      fullWidth
                    />
                  )}
                />
              )}
            />
          </Grid>

        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mt: 1 }}>
        <Box
          sx={{
            backgroundColor: "divider",
            p: 2,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">{t("Parent/Guardian Information")}</Typography>
        </Box>
        <Divider />
        <ParentFields prefix="parent1" />
      </Paper>

    </Box>
  );
}
